import { useTranslation } from 'react-i18next';
import { useMemo } from 'react';
import {
  DefaultSelect,
  Item,
} from '../../../../../../common/DefaultSelect/DefaultSelect';
import usePlanStore, {
  TVConfig,
} from '../../../../../../../../../store/planStore';

interface CopyLengthSelectProps {
  copyIndex: number;
  fetchedCopyLengths: Item[];
  isCopyLengthsFetching: boolean;
}

export default function CopyLengthSelect({
  copyIndex,
  fetchedCopyLengths,
  isCopyLengthsFetching,
}: CopyLengthSelectProps) {
  const { t } = useTranslation();
  const { countryForms, selectedCountry, updateTVConfig } = usePlanStore();
  const formData =
    countryForms[selectedCountry!].offline?.tv?.copyForm[copyIndex];

  const handleChangeStateValues = (
    key: keyof TVConfig['copyForm'][number],
    value?: string | number
  ) => {
    if (!selectedCountry || !formData) return;

    const newValue = value?.toString() ?? '';

    if (key === 'copyLength' && formData.id) {
      const idParts = formData.id.split('_');

      const newCopyLength = newValue.endsWith('s') ? newValue : `${newValue}s`;
      idParts[0] = newCopyLength;

      const updatedId = idParts.join('_');

      updateTVConfig(
        selectedCountry,
        { copyForm: [{ copyLength: newValue, id: updatedId }] },
        copyIndex
      );
    } else {
      updateTVConfig(
        selectedCountry,
        { copyForm: [{ [key]: newValue }] },
        copyIndex
      );
    }
  };

  const initialCopyLength = useMemo(() => {
    if (!formData?.copyLength && fetchedCopyLengths.length > 0) {
      const defaultCopyLength = fetchedCopyLengths[0].value;
      handleChangeStateValues('copyLength', defaultCopyLength);
      return defaultCopyLength;
    }
    return formData?.copyLength ?? '';
  }, [formData?.copyLength, fetchedCopyLengths]);

  return (
    <DefaultSelect
      value={formData?.copyLength ?? ''}
      onChange={(newValue) =>
        handleChangeStateValues('copyLength', newValue.value)
      }
      items={
        fetchedCopyLengths.map((copyLength) => {
          return {
            value: copyLength.value,
            name: `${copyLength.name} s`,
          };
        }) || []
      }
      selectName={t('mediaPlanning.copyLength')}
      labelId="copy-length"
      disabled={isCopyLengthsFetching}
      isLoading={isCopyLengthsFetching}
    />
  );
}
